import React, { useEffect, useRef } from 'react';

import { Bold, Italic, Strikethrough, Underline } from 'lucide-react';

import { Button } from '@/components/shadcn/button';
import cn from '@/lib/utils/cn';

export interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  className?: string;
  placeholder?: string;
}

function exec(cmd: string, val?: string) {
  try {
    document.execCommand(cmd, false, val);
  } catch {
    // ignore
  }
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  className,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!ref.current) return;
    if (ref.current.innerHTML !== value) {
      ref.current.innerHTML = value || '';
    }
  }, [value]);

  const handleInput = () => {
    const html = ref.current?.innerHTML ?? '';
    onChange(html);
  };

  return (
    <div
      className={cn(
        'focus-within:border-primary-500 flex flex-col overflow-hidden rounded-md border',
        className,
      )}
    >
      <div
        className={cn(
          'bg-muted flex shrink-0 items-center gap-0.5 border-b p-1 dark:bg-neutral-900',
        )}
      >
        <Button
          title="Bold"
          variant="outline"
          size="icon-sm"
          onClick={() => exec('bold')}
        >
          <Bold className="h-3 w-3" />
        </Button>

        <Button
          title="Italic"
          variant="outline"
          size="icon-sm"
          onClick={() => exec('italic')}
        >
          <Italic className="h-3 w-3" />
        </Button>

        <Button
          title="Underline"
          variant="outline"
          size="icon-sm"
          onClick={() => exec('underline')}
        >
          <Underline className="h-3 w-3" />
        </Button>

        <Button
          title="Strikethrough"
          variant="outline"
          size="icon-sm"
          onClick={() => exec('strikeThrough')}
        >
          <Strikethrough className="h-3 w-3" />
        </Button>
      </div>

      <div
        ref={ref}
        className={cn(
          'min-h-0 flex-1 p-2 text-sm outline-none',
          'overflow-auto',
        )}
        contentEditable
        onInput={handleInput}
      />
    </div>
  );
};

export default RichTextEditor;
